import { ButtonVariant } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { FormProvider } from 'react-hook-form';
import Link, { LinkTarget } from 'src/components/atoms/Link';
import { PRIVACY_POLICY_URL } from 'src/features/DUP';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { TranslationKeyPrefix } from 'src/i18n/i18n';
import { useApplication } from 'src/context/ApplicationContext';
import { useFormApplicant } from 'src/hooks/useFormApplicant';
import { useSession } from 'src/context/SessionContext';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormContainer,
  Terms,
  TermsRow
} from 'src/components/DUP/molecules/WizardFormApplicant/styles';

const TERMS_PREFIX: TranslationKeyPrefix = 'dup.form.termsAndPrivacyAgreement';
const DISABLED_MSG_PREFIX: TranslationKeyPrefix = 'dup.form.disabledMsg';

const WizardFormApplicant = () => {
  const { t } = useTranslation();
  const { isTOSChecked, setIsTOSChecked } = useSession();

  const {
    form,
    isValid,
    isSaving,
    onSave,
    UnitInput,
    FirstNameInput,
    MiddleInitialInput,
    LastNameInput,
    EmailInput,
    PhoneInput,
    HavePrevSubmitRow
  } = useFormApplicant();
  const {
    steps: { onFinished },
    isMovedTerms
  } = useApplication();

  const uncheckedTerms = isMovedTerms && !isTOSChecked;
  const saveDisabled = isSaving || !isValid || uncheckedTerms;

  const onSubmit = async () => {
    if (!saveDisabled) {
      await onSave();
      onFinished();
    }
  };

  const disabledText =
    !isValid && isMovedTerms
      ? t(`${DISABLED_MSG_PREFIX}.invalidForm`)
      : uncheckedTerms
        ? t(`${DISABLED_MSG_PREFIX}.uncheckedTerms`)
        : '';

  return (
    <FormContainer style={{ paddingTop: isMovedTerms ? undefined : '1rem' }}>
      <FormProvider {...form}>
        {isMovedTerms && HavePrevSubmitRow}
        {UnitInput}
        {FirstNameInput}
        {MiddleInitialInput}
        {LastNameInput}
        {EmailInput}
        {PhoneInput}
        {!isMovedTerms && HavePrevSubmitRow}
      </FormProvider>
      {isMovedTerms && <TermsCheck isChecked={isTOSChecked} onChange={setIsTOSChecked} />}
      <ToolTip direction={ToolTipDirection.top} content={disabledText}>
        <Button
          name="submit"
          isDisabled={saveDisabled}
          onClick={() => onSubmit()}
          variant={ButtonVariant.contained}
        >
          {t('buttonLabel.next')}
        </Button>
      </ToolTip>
    </FormContainer>
  );
};

export default WizardFormApplicant;

export const TermsCheck = ({
  isChecked,
  onChange
}: {
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { isMovedTerms } = useApplication();

  return (
    <TermsRow style={isMovedTerms ? undefined : { margin: 'unset', marginBottom: '2rem' }}>
      <Checkbox
        key={`terms_${isChecked}_`}
        onClick={(obj) => onChange(obj.isChecked)}
        name="tosAndPP"
        showLabel={false}
        value="yes"
        label={t(`${TERMS_PREFIX}.full`)}
        isChecked={isChecked}
      />
      <Terms className="label">
        {t(`${TERMS_PREFIX}.part1`)}
        <Link to="/terms" isExternal target={LinkTarget.blank}>
          {t(`${TERMS_PREFIX}.part2`)}
        </Link>
        {t(`${TERMS_PREFIX}.part3`)}
        <Link to={PRIVACY_POLICY_URL} isExternal target={LinkTarget.blank}>
          {t(`${TERMS_PREFIX}.part4`)}
        </Link>
      </Terms>
    </TermsRow>
  );
};
